import React, { useLayoutEffect, useRef } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@material-ui/core";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import CustomAcordion from "../../layout/CustomAcordion";
import HaklaiResults2990Panel from "./form2990/HaklaiResults2990Panel";
import HaklaiNoTaxOutcome from "../HaklaiNoTaxOutcome";
import { HAKLAI_PTOR_TYPES } from "../calc/shared/haklaiConstants";
import { isHaklaiAnalysisFullyExempt } from "../domain/haklaiAnalysisOutcome";
import {
  HAKLAI_ANALYSIS_RESULTS_LABELS,
  HAKLAI_ANALYSIS_RESULTS_MESSAGES
} from "./haklaiAnalysisResultsTexts";
import {
  buildHaklaiPayableTaxDisplayByPropertyKey,
  formatHaklaiWholeMoney
} from "../calc/shared/haklaiMoneyHelpers";

/**
 * Owns the Haklai analysis result screen.
 *
 * Responsibilities:
 * - renders calculation summaries returned from analysis
 * - exposes the generated 2990 form panel
 * - keeps result formatting separate from the editable calculator sections
 */

const PROPERTY_TYPE_LABELS = {
  residential: HAKLAI_ANALYSIS_RESULTS_LABELS.residential,
  commercial: HAKLAI_ANALYSIS_RESULTS_LABELS.commercial,
  commercialBg: HAKLAI_ANALYSIS_RESULTS_LABELS.commercial,
  landAndShared: HAKLAI_ANALYSIS_RESULTS_LABELS.landAndShared,
  farmRemainder: HAKLAI_ANALYSIS_RESULTS_LABELS.farmRemainder
};

const PURCHASE_DEDUCTION_TYPES = new Set(["purchase", "inheritance", "gift"]);

const columns = [
  { key: "propertyType", label: HAKLAI_ANALYSIS_RESULTS_LABELS.propertyType, align: "right" },
  { key: "saleValue", label: HAKLAI_ANALYSIS_RESULTS_LABELS.saleValue },
  { key: "nominalAcquisitionPrice", label: HAKLAI_ANALYSIS_RESULTS_LABELS.nominalAcquisitionPrice, pair: "start" },
  { key: "indexedAcquisitionPrice", label: HAKLAI_ANALYSIS_RESULTS_LABELS.indexedAcquisitionPrice, pair: "end" },
  { key: "nominalExpenses", label: HAKLAI_ANALYSIS_RESULTS_LABELS.nominalExpenses, pair: "start" },
  { key: "indexedExpenses", label: HAKLAI_ANALYSIS_RESULTS_LABELS.indexedExpenses, pair: "end" },
  { key: "depreciation", label: HAKLAI_ANALYSIS_RESULTS_LABELS.depreciation },
  { key: "totalShevach", label: HAKLAI_ANALYSIS_RESULTS_LABELS.propertyShevach, isTotal: true },
  { key: "capitalGainsTax", label: HAKLAI_ANALYSIS_RESULTS_LABELS.partTax, isTotal: true }
];

function getResultsColumnClassName(column, { includeWidth = false } = {}) {
  return [
    includeWidth
      ? (column.key === "propertyType" ? "haklai-results-property-col" : "haklai-results-amount-col")
      : "",
    column.pair ? "haklai-results-pair" : "",
    column.pair === "start" ? "haklai-results-pair-start" : "",
    column.pair === "end" ? "haklai-results-pair-end" : ""
  ].filter(Boolean).join(" ");
}


function formatCurrency(value) {
  return formatHaklaiWholeMoney(value, { empty: "-" });
}

function placeHaklaiPropertyTotal(block) {
  const table = block.querySelector("table");
  const strip = block.querySelector(".haklai-results-total-strip");
  if (!table || !strip) return;
  const stripBox = strip.getBoundingClientRect();
  const heads = [...table.querySelectorAll("thead th")].slice(-3);
  const cells = [...strip.children];
  heads.forEach((head, index) => {
    const cell = cells[index];
    if (!cell) return;
    const box = head.getBoundingClientRect();
    const isTableEdge = index === heads.length - 1;
    cell.style.left = `${box.left - stripBox.left - (isTableEdge ? 1 : 0)}px`;
    cell.style.width = `${box.width + (isTableEdge ? 1 : 0)}px`;
  });
  if (cells[0]) strip.style.height = `${cells[0].offsetHeight}px`;
}

function getDisplayedColumnAmount(row, column, {
  shevachDisplayByPropertyKey,
  nominalExpensesByPropertyKey
}) {
  if (column.key === "totalShevach") {
    return shevachDisplayByPropertyKey[row.propertyKey] ?? row.totalShevach;
  }
  if (column.key === "nominalExpenses") {
    return Number.isFinite(Number(row.nominalExpenses))
      ? row.nominalExpenses
      : nominalExpensesByPropertyKey[row.propertyKey];
  }
  return row[column.key];
}

function sumDisplayedColumn(rows, column, displayContext) {
  return rows.reduce((sum, row) => {
    const numericValue = Number(getDisplayedColumnAmount(row, column, displayContext));
    return sum + (Number.isFinite(numericValue) ? Math.round(numericValue) : 0);
  }, 0);
}

function getPropertyDisplay(row) {
  const typeLabel = PROPERTY_TYPE_LABELS[row.propertyType] || row.propertyType || HAKLAI_ANALYSIS_RESULTS_LABELS.genericAsset;
  if (row.propertyLabel && row.propertyLabel === typeLabel) return typeLabel;
  return row.propertyLabel ? `${typeLabel} - ${row.propertyLabel}` : typeLabel;
}

function normalizePtorType(ptorType) {
  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE) return HAKLAI_PTOR_TYPES.SINGLE;
  if (ptorType === HAKLAI_PTOR_TYPES.INHERITANCE) return HAKLAI_PTOR_TYPES.INHERITANCE;
  return HAKLAI_PTOR_TYPES.NONE;
}

function getAnalyzedPtorMeta(row, analyzedPreCalc) {
  const parts = Array.isArray(analyzedPreCalc?.preCalcPartsData)
    ? analyzedPreCalc.preCalcPartsData
    : [];
  const part = parts.find((candidate) => candidate?.haklaiPropertyKey === row?.propertyKey);
  if (!part) {
    return {
      hasPtorMetadata: false,
      ptorType: HAKLAI_PTOR_TYPES.NONE,
      hasTzinunOnPtor: false
    };
  }

  if (Object.prototype.hasOwnProperty.call(part, "haklaiPtorType")) {
    return {
      hasPtorMetadata: true,
      ptorType: normalizePtorType(part.haklaiPtorType),
      hasTzinunOnPtor: part.hasTzinunOnPtor === true
    };
  }

  if (part.isSinglePropertyPtorAllowed === true) {
    return {
      hasPtorMetadata: true,
      ptorType: HAKLAI_PTOR_TYPES.SINGLE,
      hasTzinunOnPtor: part.hasTzinunOnPtor === true
    };
  }

  if (part.isInheritancePtorAllowed === true) {
    return {
      hasPtorMetadata: true,
      ptorType: HAKLAI_PTOR_TYPES.INHERITANCE,
      hasTzinunOnPtor: false
    };
  }

  const hasPtorMetadata = (
    Object.prototype.hasOwnProperty.call(part, "isSinglePropertyPtorAllowed")
    || Object.prototype.hasOwnProperty.call(part, "isInheritancePtorAllowed")
  );
  return {
    hasPtorMetadata,
    ptorType: HAKLAI_PTOR_TYPES.NONE,
    hasTzinunOnPtor: false
  };
}

function getPropertyExemptionLabel(row, { haklaiDraftPreCalc, analyzedPreCalc } = {}) {
  const analyzedPtorMeta = getAnalyzedPtorMeta(row, analyzedPreCalc);
  const hasRowPtorType = Object.prototype.hasOwnProperty.call(row || {}, "ptorType");
  const rowPtorType = normalizePtorType(row?.ptorType);
  const ptorType = hasRowPtorType
    ? rowPtorType
    : analyzedPtorMeta.hasPtorMetadata
      ? analyzedPtorMeta.ptorType
      : row?.propertyKey === "residence_1"
        ? normalizePtorType(haklaiDraftPreCalc?.ptorType)
        : HAKLAI_PTOR_TYPES.NONE;
  const hasTzinunOnPtor = analyzedPtorMeta.hasTzinunOnPtor
    || (
      row?.propertyKey === "residence_1"
      && !analyzedPtorMeta.hasPtorMetadata
      && ptorType === normalizePtorType(haklaiDraftPreCalc?.ptorType)
      && haklaiDraftPreCalc?.hasTzinunOnPtor === true
    );

  if (ptorType === HAKLAI_PTOR_TYPES.NONE) {
    return "";
  }

  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE && hasTzinunOnPtor) {
    return "";
  }

  if (ptorType === HAKLAI_PTOR_TYPES.SINGLE) {
    return HAKLAI_ANALYSIS_RESULTS_LABELS.singleApartmentExemption;
  }

  if (ptorType === HAKLAI_PTOR_TYPES.INHERITANCE) {
    return HAKLAI_ANALYSIS_RESULTS_LABELS.inheritanceExemption;
  }

  return "";
}

function getNominalDeductionAmount(deduction) {
  const convertedAmount = Number(deduction?.amountAfterCurrencyConversion);
  if (Number.isFinite(convertedAmount) && convertedAmount !== 0) return convertedAmount;
  const amount = Number(deduction?.amount);
  return Number.isFinite(amount) ? amount : 0;
}

function getNominalExpensesByPropertyKey(mainCalc) {
  const groups = mainCalc?.calculatedData?.deductionMat;
  if (!Array.isArray(groups)) return {};
  return groups.reduce((byKey, deductions) => {
    if (!Array.isArray(deductions) || deductions.length === 0) return byKey;
    const propertyKey = deductions[0]?.haklaiPropertyKey;
    if (!propertyKey) return byKey;
    byKey[propertyKey] = deductions.reduce((sum, deduction) => (
      PURCHASE_DEDUCTION_TYPES.has(deduction?.deduType)
        ? sum
        : sum + getNominalDeductionAmount(deduction)
    ), 0);
    return byKey;
  }, {});
}

function getDataPerPart(mainCalc) {
  return Array.isArray(mainCalc?.calculatedData?.dataPerPart) ? mainCalc.calculatedData.dataPerPart : [];
}

export function getHaklaiResultsPrisaData(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (acc, part) => {
      const prisaYears = Array.isArray(part.prisaData)
        ? part.prisaData.reduce((maxYears, personPrisaData) => (
          Math.max(maxYears, Array.isArray(personPrisaData) ? personPrisaData.length : 0)
        ), 0)
        : 0;
      acc.maxPrisaYearsAllowed = Math.max(acc.maxPrisaYearsAllowed, prisaYears);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa47 || 0) - (part.finalLinear47 || 0);
      acc.totalPrisaRev += (part.finalMutavBeforePrisa25 || 0) - (part.finalMutav25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa25 || 0) - (part.finalLinear25 || 0);
      acc.totalPrisaRev += (part.finalLinearBeforePrisa20 || 0) - (part.finalLinear20 || 0);
      acc.totalBeforePrisa += (part.finalLinearBeforePrisa47 || 0)
        + (part.finalMutavBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa20 || 0)
        + (part.finalInflationLinear10 || 0);
      acc.totalPreAfkaa += (part.finalLinearTaxAllPreAfkaa21 || 0) + (part.finalMutavTaxAllPreAfkaa21 || 0);
      return acc;
    },
    { totalPrisaRev: 0, maxPrisaYearsAllowed: 0, totalBeforePrisa: 0, totalPreAfkaa: 0 }
  );
}

function getAfkaaCredit(mainCalc) {
  return getDataPerPart(mainCalc).reduce(
    (sum, part) => sum + (part.finalLinearAfkaaZikui22 || 0) + (part.finalMutavAfkaaZikui22 || 0),
    0
  );
}

const OFFSET_TAX_BUCKETS = [
  { suffix: "Linear47", percentField: "firstLinearPercent", fallback: 0.47 },
  { suffix: "Linear20", percentField: "secondLinearPercent", fallback: 0.2 },
  { suffix: "Linear25", percentField: "thirdLinearPercent", fallback: 0.25 },
  { suffix: "Mutav25", percentField: "mutavPercent", fallback: 0.25 },
  { suffix: "Inflation10", percentField: "inflationPercent", fallback: 0.1 }
];

function readPartTaxRate(part, fieldName, fallback) {
  const raw = part?.[fieldName];
  const parsed = typeof raw === "string" ? parseFloat(raw.replace("%", "")) : Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return parsed > 1 ? parsed / 100 : parsed;
}

function getPositiveAmount(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue > 0 ? numericValue : 0;
}

function getRabinovichSavings(mainCalc) {
  const hasPrisa = mainCalc?.prisa?.shouldTryPrisa === true;
  return getDataPerPart(mainCalc).reduce((sum, part) => {
    const percent = Number(part?.discountRabPercent) || 0;
    if (!(percent > 0) || percent >= 1) return sum;
    const discountedTax = hasPrisa
      ? (part.finalLinearBeforePrisa47 || 0)
        + (part.finalMutavBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa25 || 0)
        + (part.finalLinearBeforePrisa20 || 0)
      : (part.finalLinear47 || 0)
        + (part.finalMutav25 || 0)
        + (part.finalLinear25 || 0)
        + (part.finalLinear20 || 0);
    if (!(discountedTax > 0)) return sum;
    return sum + (discountedTax * percent) / (1 - percent);
  }, 0);
}

function getOffsetTaxSavings(mainCalc, offsetPrefix) {
  return getDataPerPart(mainCalc).reduce((sum, part) => (
    sum + OFFSET_TAX_BUCKETS.reduce((bucketSum, bucket) => {
      const offsetShevach = Number(part?.[`${offsetPrefix}${bucket.suffix}`]) || 0;
      if (!(offsetShevach > 0)) return bucketSum;
      return bucketSum + offsetShevach * readPartTaxRate(part, bucket.percentField, bucket.fallback);
    }, 0)
  ), 0);
}

export function getHaklaiResultsVariantRows(mainCalc) {
  const hasPrisa = mainCalc?.prisa?.shouldTryPrisa === true;
  const hasAfkaa = mainCalc?.afkaa === true;
  const prisaSavings = hasPrisa ? getHaklaiResultsPrisaData(mainCalc).totalPrisaRev : 0;

  return [
    {
      key: "rabinovichDiscount",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.rabinovichDiscount,
      amount: getRabinovichSavings(mainCalc)
    },
    {
      key: "businessLossOffset",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.businessLossOffset,
      amount: getOffsetTaxSavings(mainCalc, "kizuzIski")
    },
    {
      key: "capitalLossOffset",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.capitalLossOffset,
      amount: getOffsetTaxSavings(mainCalc, "kizuzHoni")
    },
    {
      key: "prisaSavings",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.prisaSavings,
      amount: getPositiveAmount(prisaSavings)
    },
    {
      key: "afkaaCredit",
      label: HAKLAI_ANALYSIS_RESULTS_LABELS.afkaaCredit,
      amount: hasAfkaa ? getPositiveAmount(getAfkaaCredit(mainCalc)) : 0
    }
  ];
}

const HaklaiAnalysisResultsView = ({
  dealId,
  haklaiAnalysis,
  haklaiDraftPreCalc,
  analyzedPreCalc,
  analyzedMainCalc,
  onBackToCalc
}) => {
  const rows = Array.isArray(haklaiAnalysis?.resultsByProperty) ? haklaiAnalysis.resultsByProperty : [];
  const summary = haklaiAnalysis?.summary || {};
  const summaryRaw = haklaiAnalysis?.summaryRaw || {};
  const nominalExpensesByPropertyKey = getNominalExpensesByPropertyKey(analyzedMainCalc);
  let shevachDisplayByPropertyKey = {};
  let hasInconsistentResults = false;
  const rowsHaveShevach = rows.every((row) => Number.isFinite(Number(row?.totalShevach)));
  try {
    if (rowsHaveShevach) {
      shevachDisplayByPropertyKey = buildHaklaiPayableTaxDisplayByPropertyKey(
        rows,
        summaryRaw.totalShevach ?? summary.totalShevach ?? 0,
        "totalShevach"
      );
    }
  } catch {
    hasInconsistentResults = true;
  }
  const isFullyExemptAnalysis = isHaklaiAnalysisFullyExempt(analyzedPreCalc);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [dealId, haklaiAnalysis?.generatedAt]);

  const columnDisplayContext = {
    shevachDisplayByPropertyKey,
    nominalExpensesByPropertyKey
  };
  const totalShevach = sumDisplayedColumn(
    rows,
    columns.find((column) => column.key === "totalShevach"),
    columnDisplayContext
  );
  const totalCapitalGainsTax = sumDisplayedColumn(
    rows,
    columns.find((column) => column.key === "capitalGainsTax"),
    columnDisplayContext
  );
  const variantRows = getHaklaiResultsVariantRows(analyzedMainCalc);
  const totalTaxToPay = summaryRaw.totalTaxToPay ?? summary.totalTaxToPay;
  const propertyBlockRef = useRef(null);

  useLayoutEffect(() => {
    const block = propertyBlockRef.current;
    if (!block) return undefined;
    const place = () => placeHaklaiPropertyTotal(block);
    place();
    if (typeof ResizeObserver === "undefined") return undefined;
    const table = block.querySelector("table");
    if (!table) return undefined;
    const observer = new ResizeObserver(place);
    observer.observe(table);
    return () => observer.disconnect();
  }, [rows, totalShevach, totalCapitalGainsTax]);
  return (
    <Box className="haklai-results deal-sum-calc">
      <Box className="haklai-results-toolbar">
        <Button className="haklai-results-back" onClick={onBackToCalc}>
          <KeyboardArrowRightIcon />
          <span>{HAKLAI_ANALYSIS_RESULTS_LABELS.backToCalc}</span>
        </Button>
      </Box>

      {hasInconsistentResults ? (
        <Box className="haklai-results-empty">
          <Typography variant="body1">
            {HAKLAI_ANALYSIS_RESULTS_MESSAGES.inconsistentResults}
          </Typography>
        </Box>
      ) : !rows.length ? (
        <Box className="haklai-results-empty">
          <Typography variant="body1">
            {HAKLAI_ANALYSIS_RESULTS_MESSAGES.noResults}
          </Typography>
        </Box>
      ) : (
        <div className="pre-calc-sections haklai-results-sections">
          <CustomAcordion summaryText={HAKLAI_ANALYSIS_RESULTS_LABELS.resultsTitle} defaultExpanded={true}>
            <div className="haklai-results-property-block" ref={propertyBlockRef}>
            <TableContainer className="haklai-results-table haklai-data-table haklai-results-property-table">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={column.key}
                        align={column.key === "propertyType" ? "right" : "center"}
                        className={getResultsColumnClassName(column, { includeWidth: true })}
                      >
                        {column.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => {
                    const propertyExemptionLabel = getPropertyExemptionLabel(row, {
                      haklaiDraftPreCalc,
                      analyzedPreCalc
                    });
                    return (
                      <TableRow key={row.propertyKey}>
                        <TableCell align="right" className="haklai-results-property-col">
                          <div className="haklai-results-property">
                            <Typography variant="body2" className="haklai-results-property-name">
                              {getPropertyDisplay(row)}
                            </Typography>
                            {propertyExemptionLabel ? (
                              <Typography variant="caption" className="haklai-results-property-meta">
                                {propertyExemptionLabel}
                              </Typography>
                            ) : null}
                          </div>
                        </TableCell>
                        {columns.slice(1).map((column) => {
                          const amount = getDisplayedColumnAmount(row, column, {
                            shevachDisplayByPropertyKey,
                            nominalExpensesByPropertyKey
                          });
                          return (
                            <TableCell
                              key={column.key}
                              align="center"
                              className={getResultsColumnClassName(column) || undefined}
                            >
                              <Typography
                                variant="body2"
                                className={`haklai-results-amount${column.isTotal ? " is-total" : ""}`}
                              >
                                {formatCurrency(amount)}
                              </Typography>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <div className="haklai-results-total-strip">
              <div className="haklai-results-property-name">
                {HAKLAI_ANALYSIS_RESULTS_LABELS.tableTotal}
              </div>
              <div className="haklai-results-amount is-total">
                {formatCurrency(totalShevach)}
              </div>
              <div className="haklai-results-amount is-total">
                {formatCurrency(totalCapitalGainsTax)}
              </div>
            </div>
            </div>
            <div className="haklai-results-variant-block">
              <div className="haklai-results-variant-title">
                {HAKLAI_ANALYSIS_RESULTS_LABELS.calcDetails}
              </div>
              <TableContainer className="haklai-results-table haklai-results-variant-table haklai-data-table">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell align="right" className="haklai-results-variant-label">
                        {HAKLAI_ANALYSIS_RESULTS_LABELS.variantKind}
                      </TableCell>
                      <TableCell align="center" className="haklai-results-variant-value">
                        {HAKLAI_ANALYSIS_RESULTS_LABELS.variantValue}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {variantRows.map((variantRow) => (
                      <TableRow key={variantRow.key}>
                        <TableCell align="right" className="haklai-results-variant-label">
                          {variantRow.label}
                        </TableCell>
                        <TableCell align="center" className="haklai-results-variant-value">
                          <Typography variant="body2" className="haklai-results-amount">
                            {formatCurrency(variantRow.amount)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="haklai-results-total-row">
                      <TableCell align="right" className="haklai-results-variant-label">
                        <Typography variant="body2" className="haklai-results-property-name">
                          {HAKLAI_ANALYSIS_RESULTS_LABELS.totalTaxToPay}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="haklai-results-variant-value">
                        <Typography variant="body2" className="haklai-results-amount is-total">
                          {formatCurrency(totalTaxToPay)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </CustomAcordion>

          {isFullyExemptAnalysis ? (
            <HaklaiNoTaxOutcome />
          ) : (
            <HaklaiResults2990Panel
              dealId={dealId}
              haklaiDraftPreCalc={haklaiDraftPreCalc}
              analyzedPreCalc={analyzedPreCalc}
            />
          )}
        </div>
      )}
    </Box>
  );
};

export default React.memo(HaklaiAnalysisResultsView);
